import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';

import { Logger } from '@futrli/shared-services';
import { UILoader, toastError } from '@futrli/ui-components';
import { ReportScheduleItem } from '@futrli-sdk/report_scheduling';

import { Data, TABLE_COLUMN } from '../../../../utils/table-filters/filter-table-types';
import { Filter } from '../../../../utils/table-filters/components/filter';
import { getHoursMinutes } from '../../../../utils/dates';
import { getPeriodText } from '../utils';
import { getScheduledReports } from '../../../../core/api/reporting/report-scheduling';
import { initialFilterTableState } from '../../../../utils/__mocks__/table-filters';
import { useLocalStorageTableFilters } from '../../../../utils/table-filters/hooks/use-local-storage-table-filters/use-local-storage-table-filters';
import { useMembersContext } from '../../../members-context';
import { useTableFilters } from '../../../../utils/table-filters/hooks/use-table-filters/use-table-filters';
import { VirtualizedList, VIRTUALIZED_LIST_TYPE } from '../../../../ui/virtualized-list/virtualized-list';

import * as styles from '../document-library.scss';
import { usePredictionsInfoContext } from '../../../../forecast/detail/context/predictions-info-context';

export interface NormalizedReportScheduleItem
  extends Omit<ReportScheduleItem, 'next_generate_at' | 'users' | 'period'> {
  next_generate_at: Date;
  users: string;
  time: string;
  period: string;
}

const columns = [
  {
    display_name: <FormattedMessage id="reporting.report_name_linked" defaultMessage="Report name (linked)" />,
    name: TABLE_COLUMN.REPORT_NAME,
    classes: clsx(styles.flex3)
  },
  {
    display_name: <FormattedMessage id="reporting.on_date" defaultMessage="On date" />,
    name: TABLE_COLUMN.NEXT_GENERATE_AT,
    classes: clsx(styles.flex1)
  },
  {
    display_name: <FormattedMessage id="reporting.time" defaultMessage="Time" />,
    name: TABLE_COLUMN.TIME,
    classes: clsx(styles.flex1)
  },
  {
    display_name: <FormattedMessage id="common.repeating" defaultMessage="Repeating" />,
    name: TABLE_COLUMN.PERIOD,
    classes: clsx(styles.flex2)
  },
  {
    display_name: <FormattedMessage id="reporting.sending_to" defaultMessage="Sending to" />,
    name: TABLE_COLUMN.SENDING_TO,
    classes: clsx(styles.flex5)
  }
];

const filterColumns = [TABLE_COLUMN.NEXT_GENERATE_AT];

const MIN_QUERY_LENGTH = 3;

const LOCAL_STORAGE_KEY = 'scheduled-reports-filters';

export const ScheduledReports = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<NormalizedReportScheduleItem[]>();
  const { isConsolidation } = usePredictionsInfoContext();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { isLoading: isMembersLoading } = useMembersContext();

  const {
    state: filterTableState,
    setInitialData,
    setInitialFiltersAndSort,
    handleCloseFilter,
    handleOpenFilter,
    handleClearColumnFilter,
    handleSortClick,
    handleSubmit
  } = useTableFilters(initialFilterTableState);

  useLocalStorageTableFilters(
    LOCAL_STORAGE_KEY,
    organisationId,
    filterTableState,
    filterColumns,
    setInitialFiltersAndSort
  );

  const { filterAnchorElement, activeColumn, sortByColumn, filters, sort, displayedData } = filterTableState;

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const fetchedData = await getScheduledReports(workspaceId, organisationId, undefined, isConsolidation);
      // setData(fetchedData);
      return fetchedData.map(d => ({
        ...d,
        // TODO: what to use when  next_generate_at is null
        next_generate_at: d.next_generate_at
          ? new Date(d.next_generate_at)
          : d.end_date
          ? new Date(d.end_date)
          : new Date(d.start_date),
        users: d.users
          .map(u => u.email_address)
          .join(', ')
          .trim(),
        time: d.next_generate_at
          ? getHoursMinutes(d.next_generate_at)
          : d.end_date
          ? getHoursMinutes(d.end_date)
          : getHoursMinutes(d.start_date),
        period: getPeriodText(d.period, d.repeating_frequency)
      }));
    } catch (e) {
      Logger.error(e as string);
      toastError();
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, organisationId, isConsolidation]);

  const fetchInitialData = useCallback(async () => {
    const initialData = await fetchData();
    setData(initialData);
    setInitialData((initialData as unknown) as Data[]);
  }, [fetchData, setInitialData]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const onClearColumnFilter = useCallback(() => handleClearColumnFilter(initialFilterTableState.filters), [
    handleClearColumnFilter
  ]);

  if (isLoading || isMembersLoading) {
    return (
      <div>
        <UILoader isFull />
      </div>
    );
  }

  return (
    <div data-testid="scheduledReportsContent">
      <h1 className={styles.title}>
        <FormattedMessage id="reporting.scheduled_reports" defaultMessage="Scheduled reports" />
      </h1>
      <div className={styles.desc} style={{ marginBottom: 20 }}>
        <FormattedMessage
          id="reporting.pdf_report_will_be_sent"
          defaultMessage="Report will be sent as a PDF, with a link to the live report."
        />
      </div>
      <VirtualizedList
        data={displayedData}
        handleTableFilterClick={handleOpenFilter}
        filters={filters}
        sortByColumn={sortByColumn}
        type={VIRTUALIZED_LIST_TYPE.SCHEDULED_REPORTS}
        columns={columns}
        revalidateData={fetchInitialData}
      />
      <Filter
        data={(data as unknown) as Data[]}
        anchorElement={filterAnchorElement}
        handleSubmit={handleSubmit}
        handleClose={handleCloseFilter}
        handleClearColumnFilter={onClearColumnFilter}
        activeColumn={activeColumn}
        sortByColumn={sortByColumn}
        filters={filters}
        handleSortClick={handleSortClick}
        sort={sort}
        minQueryLength={MIN_QUERY_LENGTH}
      />
    </div>
  );
};
